/**
 * HUD for the drop (08 §2.6): the jump light, the three pods and what became of them,
 * altitude under canopy, the wind, and what the buttons do right now.
 */

import './flyover.css';
import type { MissionDef, World } from '../../sim/types';

const POD_LETTERS = ['A', 'B', 'C'] as const;
const POD_STATE: Record<string, string> = {
  aboard: 'ABOARD', falling: 'AWAY', landed: 'DOWN', destroyed: 'LOST', found: 'TAKEN', emptied: 'EMPTY',
};

export class FlyoverHud {
  private readonly root: HTMLDivElement;
  private readonly light: HTMLDivElement;
  private readonly hint: HTMLDivElement;
  private readonly altitude: HTMLDivElement;
  private readonly pods: HTMLDivElement[] = [];
  private last = '';

  constructor(parent: HTMLElement, def: MissionDef) {
    this.root = document.createElement('div');
    this.root.className = 'fly-root';
    this.light = this.add('fly-light');
    this.hint = this.add('fly-hint');
    this.altitude = this.add('fly-alt');
    const rack = this.add('fly-pods');
    for (const letter of POD_LETTERS) {
      const chip = document.createElement('div');
      chip.className = 'fly-pod';
      chip.dataset['letter'] = letter;
      rack.append(chip);
      this.pods.push(chip);
    }
    const wind = this.add('fly-wind');
    const speed = Math.hypot(def.wind.x, def.wind.z);
    // Screen-up is north, so a wind blowing toward +z (south) points down the screen.
    const deg = Math.round((Math.atan2(def.wind.x, -def.wind.z) * 180) / Math.PI);
    wind.innerHTML = `WIND <span class="fly-arrow" style="transform:rotate(${deg}deg)">&#8593;</span> ${speed.toFixed(1)} M/S`;
    parent.append(this.root);
  }

  private add(className: string): HTMLDivElement {
    const el = document.createElement('div');
    el.className = className;
    this.root.append(el);
    return el;
  }

  layout(rect: { left: number; top: number; width: number; height: number }, scale: number): void {
    const s = this.root.style;
    s.left = `${rect.left}px`;
    s.top = `${rect.top}px`;
    s.transform = `scale(${scale})`;
  }

  update(world: World): void {
    const ins = world.insertion;
    const visible = ins.phase !== 'ground' && !world.result;
    this.root.style.display = visible ? 'block' : 'none';
    if (!visible) return;

    const key = `${ins.phase}|${ins.greenLight}|${Math.round(ins.altitude)}|${ins.pods.map((p) => p.state).join(',')}`;
    if (key === this.last) return;
    this.last = key;

    const flying = ins.phase === 'flyover';
    this.light.className = `fly-light ${flying ? (ins.greenLight ? 'fly-go' : 'fly-wait') : 'fly-canopy'}`;
    this.light.textContent = flying ? (ins.greenLight ? 'GREEN LIGHT - JUMP' : 'STAND BY') : 'UNDER CANOPY';
    const aboard = ins.pods.some((p) => p.state === 'aboard');
    this.hint.textContent = flying
      ? ins.greenLight ? 'FIRE: JUMP     A / D: STEER THE AIRCRAFT' : aboard ? 'FIRE: KICK OUT A POD     A / D: STEER THE AIRCRAFT' : 'A / D: STEER THE AIRCRAFT'
      : 'A / D: STEER   -   TURNING COSTS HEIGHT';
    this.altitude.textContent = flying ? '' : `ALT ${String(Math.round(ins.altitude)).padStart(2, '0')} M`;
    this.pods.forEach((chip, i) => {
      const pod = ins.pods.find((p) => p.index === i);
      chip.textContent = `${POD_LETTERS[i] ?? '?'} ${pod ? (POD_STATE[pod.state] ?? pod.state) : '--'}`;
      chip.className = `fly-pod ${pod ? `fly-pod-${pod.state}` : 'fly-pod-none'}`;
    });
  }

  dispose(): void {
    this.root.remove();
  }
}
